import { useI18n } from '@upweo/ui';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  SkeletonRows,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
} from '@upweo/ui';
import type { CustomerDetail } from '@upweo/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { ApiClientError } from '../lib/api-client.js';
import * as api from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';
import { PageHeader } from './PageHeader.js';

const Stat = ({ label, value }: { label: string; value: string }): React.JSX.Element => (
  <div>
    <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="tabular mt-0.5">{value}</div>
  </div>
);

const CustomerDrawer = ({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}): React.JSX.Element => {
  const { t } = useI18n();
  const { data, isPending, isError } = useQuery<CustomerDetail>({
    queryKey: ['customer', id],
    queryFn: () => api.getCustomer(id),
  });

  const name = data ? [data.firstName, data.lastName].filter(Boolean).join(' ') || data.email : '';

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>{name || 'Customer'}</CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </CardHeader>
      <CardContent className="space-y-5 text-sm">
        {isPending && <p className="text-muted-foreground">Loading…</p>}
        {isError && <ErrorState message="Could not load this customer." />}

        {data && (
          <>
            <div className="grid gap-4 sm:grid-cols-4">
              <Stat label={t('common.email')} value={data.email} />
              <Stat label={t('common.phone')} value={data.phone ?? '—'} />
              <Stat label={t('customers.columnOrders')} value={String(data.orderCount)} />
              <Stat
                label={t('customers.lifetimeSpend')}
                value={api.formatMoney(data.totalSpent, data.currencyCode)}
              />
            </div>

            {data.addresses.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Addresses
                </div>
                <ul className="mt-1 space-y-1 text-muted-foreground">
                  {data.addresses.map((a) => (
                    <li key={a.id}>
                      {[a.address1, a.city, a.postalCode, a.countryCode].filter(Boolean).join(', ')}
                      {a.isDefault && <span className="ml-2 text-xs">(default)</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data.recentOrders.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Recent orders
                </div>
                <ul className="mt-1 space-y-1">
                  {data.recentOrders.map((o) => (
                    <li key={o.id} className="flex justify-between">
                      <span>
                        {o.name}{' '}
                        <span className="text-xs text-muted-foreground">
                          {o.financialStatus.toLowerCase()}
                        </span>
                      </span>
                      <span className="tabular">
                        {api.formatMoney(o.totalPrice, data.currencyCode)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const CustomerForm = ({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}): React.JSX.Element => {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.createCustomer({
        email,
        acceptsMarketing: false,
        tags: [],
        ...(firstName ? { firstName } : {}),
        ...(lastName ? { lastName } : {}),
      }),
    onSuccess: onSaved,
    onError: (err: unknown) => {
      setError(
        err instanceof ApiClientError
          ? (err.error.fieldErrors?.map((f) => `${f.path}: ${f.message}`).join(', ') ??
              err.error.message)
          : 'Could not save the customer',
      );
    },
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setError(null);
    create.mutate();
  };

  return (
    <form onSubmit={onSubmit} className="rounded-lg border bg-card shadow-sm">
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>{t('customers.newCustomer')}</CardTitle>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label={t('common.email')} htmlFor="c-email">
            <Input
              id="c-email"
              type="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
            />
          </Field>
          <Field label={t('common.firstName')} htmlFor="c-first">
            <Input
              id="c-first"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
              }}
            />
          </Field>
          <Field label={t('common.lastName')} htmlFor="c-last">
            <Input
              id="c-last"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
              }}
            />
          </Field>
        </div>

        {error && (
          <p
            role="alert"
            className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}

        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Saving…' : 'Create customer'}
        </Button>
      </CardContent>
    </form>
  );
};

export const CustomersPage = (): React.JSX.Element => {
  const { t } = useI18n();
  const { activeStore } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const customersQuery = useQuery({
    queryKey: ['customers', activeStore?.tenantId, search],
    queryFn: ({ signal }) => api.listCustomers({ search, signal }),
    enabled: Boolean(activeStore),
  });

  const customers = customersQuery.data?.items ?? [];

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('customers.title')}
        subtitle={activeStore ? `${customers.length} in ${activeStore.tenantName}` : 'No store'}
        action={
          <Button
            onClick={() => {
              setCreating(true);
            }}
          >
            Add customer
          </Button>
        }
      />

      <Input
        type="search"
        placeholder={t('customers.searchPlaceholder')}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
        }}
        className="max-w-sm"
      />

      {creating && (
        <CustomerForm
          onClose={() => {
            setCreating(false);
          }}
          onSaved={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['customers'] });
          }}
        />
      )}

      {openId && (
        <CustomerDrawer
          id={openId}
          onClose={() => {
            setOpenId(null);
          }}
        />
      )}

      <TableWrap>
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH>{t('customers.columnCustomer')}</TH>
              <TH>{t('common.email')}</TH>
              <TH className="text-right">{t('customers.columnOrders')}</TH>
              <TH>{t('customers.columnMarketing')}</TH>
              <TH className="text-right">Lifetime spend</TH>
            </TR>
          </THead>
          <TBody>
            {customersQuery.isPending && <SkeletonRows rows={4} cols={5} />}

            {customersQuery.isError && (
              <tr>
                <td colSpan={5}>
                  <ErrorState message="Failed to load customers" />
                </td>
              </tr>
            )}

            {!customersQuery.isPending && !customersQuery.isError && customers.length === 0 && (
              <tr>
                <td colSpan={5}>
                  <EmptyState
                    title={search ? 'No matching customers' : 'No customers yet'}
                    description={
                      search
                        ? 'Try a different search term.'
                        : 'Customers appear here after their first order, or add one manually.'
                    }
                  />
                </td>
              </tr>
            )}

            {customers.map((c) => (
              <TR
                key={c.id}
                onClick={() => {
                  setOpenId(c.id);
                }}
                className="cursor-pointer"
              >
                <TD className="font-medium">
                  {[c.firstName, c.lastName].filter(Boolean).join(' ') || '—'}
                </TD>
                <TD>{c.email}</TD>
                <TD className="tabular text-right text-muted-foreground">{c.orderCount}</TD>
                <TD className="text-muted-foreground">{c.acceptsMarketing ? 'Subscribed' : '—'}</TD>
                <TD className="tabular text-right">
                  {api.formatMoney(c.totalSpent, c.currencyCode)}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </TableWrap>
    </div>
  );
};
